import React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, UserCheckIcon } from 'lucide-react';
import { getSolution, solutionPath } from '../data/solutions';
import { BOOK_DEMO_URL, GET_STARTED_URL, VC_PATH, BRAND } from '../data/dgpt';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { PageHero } from '../components/platform/PageHero';
import { CtaButton } from '../components/ui/CtaButton';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { ProcessFlow } from '../components/platform/ProcessFlow';
import { ProcessCta } from '../components/platform/ProcessCta';
import { SolutionVisual } from '../components/solutions/SolutionVisual';
import { SolutionProof } from '../components/solutions/SolutionProof';
import { OtherSolutions } from '../components/solutions/OtherSolutions';
import { MobileStickyCta } from '../components/dgpt/MobileStickyCta';
import { NotFound } from './NotFound';
export function SolutionPage() {
  const {
    slug = ''
  } = useParams();
  const solution = getSolution(slug);
  const path = solutionPath(slug);
  usePageMeta({
    title: solution?.page.seo.title ?? 'Solution not found | DiligenceGPT™',
    description: solution?.page.seo.description ?? 'This solution page does not exist.',
    path,
    keywords: solution?.page.seo.keywords,
    jsonLd: solution ? [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Solutions',
        item: canonicalUrl('/solutions')
      }, {
        '@type': 'ListItem',
        position: 3,
        name: solution.label,
        item: canonicalUrl(path)
      }]
    }] : []
  });
  if (!solution) return <NotFound data-id="element-1614" data-mp="2r51pio134vze" />;
  const {
    page
  } = solution;
  const isInvest = solution.id === 'invest';
  return <>
      <main id="main" data-id="element-1615" data-mp="2o59nir1s0y50">
        <PageHero headingId="solution-heading" breadcrumb={solution.label} breadcrumbParent={{
        label: 'Solutions',
        href: '/solutions'
      }} eyebrow={`${solution.label} · ${solution.descriptor}`} title={page.title} description={solution.line} actions={<>
              <CtaButton href={GET_STARTED_URL} variant="violet" size="xl" event="primary_cta_click" eventParams={{
          placement: `solution_${solution.id}_hero`,
          cta: 'get_started'
        }} className="w-full sm:w-auto" data-id="element-1617" data-mp="2swhwxn33wji6">
                Get Started
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1618" data-mp="2a5rx678u1ctj" />
              </CtaButton>
              {isInvest ? <CtaButton href={VC_PATH} variant="outlineDark" size="xl" className="w-full sm:w-auto" data-id="element-1619" data-mp="3qip3h2iz0lx6">
                  Explore venture capital
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1620" data-mp="3lmzwie4mg2qu" />
                </CtaButton> : <CtaButton href={BOOK_DEMO_URL} variant="outlineDark" size="xl" event="enterprise_demo_click" eventParams={{
          placement: `solution_${solution.id}_hero`
        }} className="w-full sm:w-auto" data-id="element-1621" data-mp="1xlizmz8o668">
                  Book a Demo
                </CtaButton>}
            </>} aside={<SolutionVisual id={page.visual} data-id="element-1622" data-mp="knbigndy5g4h" />} data-id="element-1616" data-mp="2o4rgczdkszib" />

        <section aria-labelledby="audience-heading" className="border-b border-hairline-soft bg-surface" data-id="element-1623" data-mp="6wy6m9xxph9v">
          <div className="mx-auto grid w-full max-w-[1320px] gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-20" data-id="element-1624" data-mp="aybeepkl7ujf">
            <div data-id="element-1625" data-mp="yn2yeto0x6l4">
              <SectionHeading eyebrow={solution.tagsLabel} headingId="audience-heading" title={`Built for ${solution.descriptor.toLowerCase()}.`} data-id="element-1626" data-mp="ymkxvkubw67e" />
              <Reveal as="ul" className="mt-6 flex flex-wrap gap-2" data-id="element-1627" data-mp="3bvnhtw4jsp93">
                {solution.tags.map(tag => <li key={tag} className="rounded-lg border border-hairline bg-panel px-3 py-1.5 text-sm font-medium text-text-primary" data-id="element-1628" data-mp="36wkk6wqwdlb5">
                    {tag}
                  </li>)}
              </Reveal>
            </div>
            <Reveal delay={0.05} className="rounded-2xl border border-hairline bg-panel p-6 sm:p-8" data-id="element-1629" data-mp="2xxyxcpee7k4k">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#9A5B13]" data-id="element-1630" data-mp="3uoiy7ohqdurz">Example workflow</p>
              <p className="mt-3 text-lg leading-relaxed text-text-primary sm:text-xl" data-id="element-1631" data-mp="3kwlm8fiweys7">{page.example}</p>
            </Reveal>
          </div>
        </section>

        <section aria-labelledby="workflow-heading" className="border-b border-hairline-soft bg-ink" data-id="element-1632" data-mp="3gu8drjcromrq">
          <div className="mx-auto w-full max-w-[1320px] px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-id="element-1633" data-mp="h93592feb736">
            <SectionHeading eyebrow="How it runs" headingId="workflow-heading" title="The example, step by step." description="Agents handle the collection, checking, follow-up and preparation. Your team reviews and decides." data-id="element-1634" data-mp="cg0bat6phvmg" />
            <Reveal className="mt-10" data-id="element-1635" data-mp="243gio6orbct9">
              <ProcessFlow steps={page.steps} ariaLabel={`${solution.descriptor} example workflow`} emphasizeLast data-id="element-1636" data-mp="246kr5g998upq" />
            </Reveal>
          </div>
        </section>

        <section aria-labelledby="output-heading" className="border-b border-hairline-soft bg-surface" data-id="element-1637" data-mp="2e1w6o0shm5yj">
          <div className="mx-auto grid w-full max-w-[1320px] gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start lg:gap-16 lg:px-8 lg:py-20" data-id="element-1638" data-mp="2izo0j0cp025d">
            <div data-id="element-1639" data-mp="2rsrbn1zv82wu">
              <SectionHeading eyebrow="Decision output" headingId="output-heading" title={page.decisionOutput.title} data-id="element-1640" data-mp="2rtaoi4ans140" />
              <Reveal as="ul" className="mt-8 flex flex-col gap-3" data-id="element-1641" data-mp="2wqsupmxak28b">
                {page.decisionOutput.items.map(item => <li key={item} className="flex items-start gap-3 text-[15px] text-text-primary sm:text-base" data-id="element-1642" data-mp="11p7hz2p6hcs1">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-soft text-lime" data-id="element-1643" data-mp="wrfgbgyqecan">
                      <CheckIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" data-id="element-1644" data-mp="1bgv36lv55xl2" />
                    </span>
                    {item}
                  </li>)}
              </Reveal>
            </div>
            <Reveal delay={0.05} className="flex gap-4 rounded-2xl border border-lime/25 bg-violet-soft p-6 sm:p-8" data-id="element-1645" data-mp="1ki3318db36h9">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-lime text-white" data-id="element-1646" data-mp="1ue603ztawexc">
                <UserCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-1647" data-mp="1pf1t9ftumta6" />
              </span>
              <div data-id="element-1648" data-mp="3w59f11a84fnp">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-lime" data-id="element-1649" data-mp="1g6lj6uh7qh">Who decides</p>
                <p className="mt-1 text-xl font-semibold tracking-[-0.015em] text-text-primary" data-id="element-1650" data-mp="3r7zpllmu43zv">{page.decisionOwner}</p>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary" data-id="element-1651" data-mp="26pffa24k60qw">
                  DiligenceGPT™ prepares the output and shows what’s still open. People review the evidence and make the decision.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {isInvest ? <section id="venture-capital" aria-labelledby="invest-vc-heading" className="dgpt-dark scroll-mt-24 border-b border-night-line bg-night" data-id="element-1652" data-mp="1ny5w114zjxm4">
            <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-8 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:py-20" data-id="element-1653" data-mp="1sy0ld0944f52">
              <SectionHeading tone="dark" eyebrow="Where we started" headingId="invest-vc-heading" title="Venture capital is our most mature workflow." description={`Thesis fit, Deal Radar, structured diligence, IC memos and portfolio monitoring. ${BRAND.tagline}`} data-id="element-1654" data-mp="1iyjhmodf8bbq" />
              <CtaButton href={VC_PATH} variant="violet" size="xl" className="w-full shrink-0 sm:w-auto" data-id="element-1655" data-mp="215tmwdmqqnm1">
                Explore venture capital
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1656" data-mp="1rd5tlsuyz1s2" />
              </CtaButton>
            </div>
          </section> : null}

        <SolutionProof organizations={page.proofOrgs} data-id="element-1657" data-mp="1mcqfv06rzs9k" />
        <OtherSolutions current={solution.id} data-id="element-1658" data-mp="1hgt0aw3c6pxu" />
        <ProcessCta placement={`solution_${solution.id}`} title={`Run ${solution.descriptor.toLowerCase()} with DiligenceGPT™.`} primaryLabel="Get Started" data-id="element-1659" data-mp="1hdex5lmumcpc" />
      </main>
      <MobileStickyCta placement={`solution_${solution.id}_mobile_sticky`} data-id="element-1660" data-mp="18flevelx5jl6" />
    </>;
}